/**
 * Funções auxiliares para a feature de troca de atendentes no chat.
 * @module chat-helpers
 */

/**
 * Cria um objeto de marcador de troca de atendente.
 *
 * @param {Object} dados - Dados para criação do marcador
 * @param {string|number} dados.atendenteAnteriorId - ID do atendente anterior (obrigatório)
 * @param {string} [dados.atendenteAnteriorNome] - Nome do atendente anterior (opcional, usa fallback)
 * @param {string|number} dados.atendenteNovoId - ID do novo atendente (obrigatório)
 * @param {string} [dados.atendenteNovoNome] - Nome do novo atendente (opcional, usa fallback)
 * @param {string|number|Date} dados.timestamp - Timestamp da troca (obrigatório)
 * @returns {Object|null} Objeto do marcador de troca ou null se parâmetros obrigatórios ausentes
 * @returns {string} return.tipo - Tipo do marcador ('troca_atendente')
 * @returns {Object} return.atendente_anterior - Dados do atendente anterior
 * @returns {string|number} return.atendente_anterior.id - ID do atendente anterior
 * @returns {string} return.atendente_anterior.nome - Nome do atendente anterior
 * @returns {Object} return.atendente_novo - Dados do novo atendente
 * @returns {string|number} return.atendente_novo.id - ID do novo atendente
 * @returns {string} return.atendente_novo.nome - Nome do novo atendente
 * @returns {string|number|Date} return.timestamp - Timestamp da troca
 * @returns {string} return.mensagemId - ID único do marcador
 *
 * @example
 * const marcador = criarMarcadorTroca({
 *   atendenteAnteriorId: 1,
 *   atendenteAnteriorNome: 'João',
 *   atendenteNovoId: 2,
 *   atendenteNovoNome: 'Maria',
 *   timestamp: Date.now()
 * });
 */
function criarMarcadorTroca(dados) {
  const {
    atendenteAnteriorId,
    atendenteAnteriorNome,
    atendenteNovoId,
    atendenteNovoNome,
    timestamp,
  } = dados || {}

  // Validação: precisa de pelo menos o novo atendente e timestamp (0 é válido para ambos)
  if (
    atendenteNovoId === undefined || atendenteNovoId === null || atendenteNovoId === ''
    || timestamp === undefined || timestamp === null || timestamp === ''
  ) {
    return null
  }

  // Usar valores padrão se anterior não estiver disponível
  const anteriorId = atendenteAnteriorId || 0
  const novoId = atendenteNovoId

  const timestampMs = timestamp instanceof Date
    ? timestamp.getTime()
    : typeof timestamp === 'string'
      ? new Date(timestamp).getTime()
      : timestamp

  const mensagemId = `troca_${anteriorId}_${novoId}_${timestampMs}`

  return {
    tipo: 'troca_atendente',
    atendente_anterior: {
      id: anteriorId,
      nome: atendenteAnteriorNome || 'Atendente anterior',
    },
    atendente_novo: {
      id: novoId,
      nome: atendenteNovoNome || 'Atendente',
    },
    timestamp,
    mensagemId,
  }
}

/**
 * Extrai o timestamp de uma mensagem, suportando múltiplos formatos de campo.
 *
 * @param {Object} mensagem - Objeto da mensagem
 * @returns {number} Timestamp em milissegundos
 * @private
 */
function extrairTimestamp(mensagem) {
  const valor = mensagem.timestamp || mensagem.created_at || mensagem.data

  if (!valor) return 0

  if (valor instanceof Date) {
    return valor.getTime()
  }

  if (typeof valor === 'string') {
    return new Date(valor).getTime()
  }

  return valor
}

/**
 * Verifica se uma mensagem do chat corresponde ao ID de referência (wamid, mensagemId interno, etc.).
 * Ignora marcadores de troca de atendente.
 *
 * @param {Object} m - Mensagem do array local
 * @param {string|number} refId - referencia_mensagem_id recebido do backend/socket
 * @returns {boolean}
 */
function mensagemCorrespondeReferencia(m, refId) {
  if (!m || m.tipo === 'troca_atendente' || refId === undefined || refId === null || refId === '') {
    return false
  }
  const ref = String(refId).trim()
  if (!ref) return false
  if (m.whatsapp_id != null && String(m.whatsapp_id).trim() === ref) return true
  if (m.mensagemId != null && String(m.mensagemId).trim() === ref) return true
  return false
}

/**
 * Busca no array local a mensagem citada pelo ID de referência.
 *
 * @param {Array<Object>} mensagens
 * @param {string|number} refId
 * @returns {Object|null}
 */
function buscarMensagemPorReferencia(mensagens, refId) {
  if (!Array.isArray(mensagens)) return null
  return mensagens.find(m => mensagemCorrespondeReferencia(m, refId)) ?? null
}

/**
 * Constrói um Map indexando mensagens por `whatsapp_id` e `mensagemId` para
 * lookups O(1) ao resolver `referencia_mensagem_id`. Marcadores de troca de
 * atendente são ignorados. Chaves vazias após trim são descartadas. Quando
 * duas mensagens compartilham a mesma chave, a última prevalece.
 *
 * @param {Array<Object>} mensagens
 * @returns {Map<string, Object>}
 */
function construirMapaCitacoes(mensagens) {
  const mapa = new Map()
  if (!Array.isArray(mensagens)) return mapa

  for (const m of mensagens) {
    if (!m) continue
    if (m.tipo === 'troca_atendente' || m._marcadorTroca === true) continue

    if (m.whatsapp_id != null) {
      const chave = String(m.whatsapp_id).trim()
      if (chave) mapa.set(chave, m)
    }

    if (m.mensagemId != null) {
      const chave = String(m.mensagemId).trim()
      if (chave) mapa.set(chave, m)
    }
  }

  return mapa
}

/**
 * Retorna novo array de mensagens enriquecidas com `mensagem_referenciada` e
 * `mensagem_referenciada_nao_encontrada` resolvidos via lookup O(1) no Map.
 * Não muta o array nem os objetos originais. Marcadores de troca são preservados.
 * Mensagens com `mensagem_referenciada` já truthy são mantidas como estão.
 *
 * @param {Array<Object>} mensagens
 * @param {Map<string, Object>} [mapa] - Pré-construído. Se omitido, gera internamente.
 * @returns {Array<Object>}
 */
function resolverCitacoes(mensagens, mapa) {
  if (!Array.isArray(mensagens)) return []

  const indice = mapa instanceof Map ? mapa : construirMapaCitacoes(mensagens)

  return mensagens.map(msg => {
    if (!msg) return msg
    if (msg.tipo === 'troca_atendente' || msg._marcadorTroca === true) return msg
    if (msg.mensagem_referenciada) return msg

    const refId = msg.referencia_mensagem_id
    if (refId === undefined || refId === null || refId === '') return msg

    const chave = String(refId).trim()
    if (!chave) return msg

    const mensagemReferenciada = indice.get(chave) || null

    return {
      ...msg,
      mensagem_referenciada: mensagemReferenciada,
      mensagem_referenciada_nao_encontrada: !mensagemReferenciada,
    }
  })
}

/**
 * Insere marcadores de troca de atendente nas posições corretas dentro do array de mensagens.
 *
 * O algoritmo percorre as mensagens uma única vez (O(n)) e insere marcadores
 * quando o timestamp da mensagem é maior ou igual à data de início de uma troca.
 * Os marcadores são inseridos ANTES da primeira mensagem que corresponde à troca.
 *
 * @param {Array<Object>} mensagens - Array de mensagens do chat
 * @param {Array<Object>} historicoAtendentes - Histórico de atendentes com datas de início
 * @param {string|number} historicoAtendentes[].atendente_id - ID do atendente
 * @param {string} [historicoAtendentes[].atendente_nome] - Nome do atendente
 * @param {string|number|Date} historicoAtendentes[].data_inicio - Data de início do atendimento
 * @returns {Array<Object>} Array combinado de mensagens e marcadores de troca
 *
 * @example
 * const mensagens = [
 *   { id: 1, texto: 'Olá', timestamp: 1000 },
 *   { id: 2, texto: 'Tudo bem?', timestamp: 2000 },
 *   { id: 3, texto: 'Posso ajudar?', timestamp: 3000 }
 * ];
 *
 * const historico = [
 *   { atendente_id: 1, atendente_nome: 'João', data_inicio: 500 },
 *   { atendente_id: 2, atendente_nome: 'Maria', data_inicio: 2500 }
 * ];
 *
 * const resultado = inserirMarcadoresTroca(mensagens, historico);
 * // Retorna: [msg1, msg2, marcadorTroca, msg3]
 */
function inserirMarcadoresTroca(mensagens, historicoAtendentes) {
  if (!Array.isArray(mensagens)) {
    return []
  }

  if (!historicoAtendentes || historicoAtendentes.length === 0) {
    return mensagens
  }

  // Normalizar campos do histórico (API pode retornar 'id' ou 'atendente_id', 'inicio' ou 'data_inicio')
  const historicoNormalizado = historicoAtendentes.map(h => ({
    atendente_id: h.atendente_id || h.id,
    atendente_nome: h.atendente_nome || h.nome,
    data_inicio: h.data_inicio || h.inicio,
  }))

  const historicoOrdenado = [...historicoNormalizado].sort((a, b) => {
    const dataA = extrairTimestamp({ timestamp: a.data_inicio })
    const dataB = extrairTimestamp({ timestamp: b.data_inicio })
    return dataA - dataB
  })

  const trocas = []

  // Primeira entrada do histórico = atribuição ao atendente (não só transferência entre dois registros)
  const primeiro = historicoOrdenado[0]
  if (primeiro && (primeiro.atendente_id != null && primeiro.atendente_id !== '')) {
    trocas.push({
      atendenteAnteriorId: 0,
      atendenteAnteriorNome: undefined,
      atendenteNovoId: primeiro.atendente_id,
      atendenteNovoNome: primeiro.atendente_nome,
      dataInicio: extrairTimestamp({ timestamp: primeiro.data_inicio }),
      inserido: false,
    })
  }

  for (let i = 1; i < historicoOrdenado.length; i++) {
    const anterior = historicoOrdenado[i - 1]
    const novo = historicoOrdenado[i]

    trocas.push({
      atendenteAnteriorId: anterior.atendente_id,
      atendenteAnteriorNome: anterior.atendente_nome,
      atendenteNovoId: novo.atendente_id,
      atendenteNovoNome: novo.atendente_nome,
      dataInicio: extrairTimestamp({ timestamp: novo.data_inicio }),
      inserido: false,
    })
  }

  trocas.sort((a, b) => a.dataInicio - b.dataInicio)

  if (trocas.length === 0) {
    return mensagens
  }

  const resultado = []
  let indiceTroca = 0

  for (const mensagem of mensagens) {
    const timestampMensagem = extrairTimestamp(mensagem)

    while (
      indiceTroca < trocas.length
      && !trocas[indiceTroca].inserido
      && timestampMensagem >= trocas[indiceTroca].dataInicio
    ) {
      const troca = trocas[indiceTroca]
      const marcador = criarMarcadorTroca({
        atendenteAnteriorId: troca.atendenteAnteriorId,
        atendenteAnteriorNome: troca.atendenteAnteriorNome,
        atendenteNovoId: troca.atendenteNovoId,
        atendenteNovoNome: troca.atendenteNovoNome,
        timestamp: troca.dataInicio,
      })

      if (marcador) {
        resultado.push(marcador)
      }

      trocas[indiceTroca].inserido = true
      indiceTroca++
    }

    resultado.push(mensagem)
  }

  while (indiceTroca < trocas.length) {
    if (!trocas[indiceTroca].inserido) {
      const troca = trocas[indiceTroca]
      const marcador = criarMarcadorTroca({
        atendenteAnteriorId: troca.atendenteAnteriorId,
        atendenteAnteriorNome: troca.atendenteAnteriorNome,
        atendenteNovoId: troca.atendenteNovoId,
        atendenteNovoNome: troca.atendenteNovoNome,
        timestamp: troca.dataInicio,
      })

      if (marcador) {
        resultado.push(marcador)
      }
    }
    indiceTroca++
  }

  return resultado
}

export {
  criarMarcadorTroca,
  inserirMarcadoresTroca,
  mensagemCorrespondeReferencia,
  buscarMensagemPorReferencia,
  construirMapaCitacoes,
  resolverCitacoes,
}

export default {
  criarMarcadorTroca,
  inserirMarcadoresTroca,
  mensagemCorrespondeReferencia,
  buscarMensagemPorReferencia,
  construirMapaCitacoes,
  resolverCitacoes,
}
